import React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatTabs from "./Chat/ChatTabs";

const DRAWER_OPEN_WIDTH  = 260;
const DRAWER_CLOSED_WIDTH = 68;

const openedMixin = (theme) => ({
  width: DRAWER_OPEN_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  width: DRAWER_CLOSED_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? DRAWER_OPEN_WIDTH : DRAWER_CLOSED_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const DrawerMenu = ({
  open,
  menuItems,
  selectedItem,
  handleItemClick,
  // ChatTabs props
  chatTabsRef,
  activeTab,
  chatContexts,
  onTabChange,
  onCloseTab,
  onCreateNewTab,
  onEditTab,
  onDeleteTab,
  onEditDefaultTab,
  defaultTabName,
  truncateTabName,
  selectedTabDetails,
  isChatLoading,
}) => {
  const getIcon = (item) => {
    if (item.text === `Campbell's Insights Generator`) {
      return <AutoAwesomeIcon fontSize="small" />;
    } else if (item.text === "Content Creator") {
      return <LightbulbIcon fontSize="small" />;
    } else if (item.icon) {
      return item.icon;
    }
    return (
      <img
        src={item.greyicon}
        alt={`${item.text} icon`}
        style={{ maxWidth: "18px" }}
      />
    );
  };

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        height: "calc(100vh - 80px)",
        zIndex: 1,
        ".MuiDrawer-paper": {
          border: "none",
          boxShadow: "2px 0 12px rgba(112, 144, 176, 0.12)",
          marginTop: "80px",
          maxHeight: "calc(100vh - 80px)",
          paddingTop: "12px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Nav items — fixed at top */}
      <List disablePadding sx={{ flexShrink: 0 }}>
        {menuItems.map((item) => {
          const isSelected = selectedItem?.id === item.id;
          return (
            <ListItem key={item.text} disablePadding sx={{ display: "block", px: 1, py: 0.5 }}>
              <ListItemButton
                onClick={() => handleItemClick(item)}
                sx={{
                  minHeight: 48,
                  borderRadius: "10px",
                  justifyContent: open ? "initial" : "center",
                  px: open ? 2 : 1.5,
                  backgroundColor: (theme) => isSelected
                    ? (theme.palette.mode === "dark" ? "rgba(123,147,209,0.15)" : "#EEF2FF")
                    : "transparent",
                  "&:hover": {
                    backgroundColor: (theme) => isSelected
                      ? (theme.palette.mode === "dark" ? "rgba(123,147,209,0.2)" : "#E0E7FF")
                      : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#F5F7FA"),
                  },
                  "& .MuiTouchRipple-root": {
                    display: "none",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 1.5 : "auto",
                    justifyContent: "center",
                    color: isSelected ? "text.primary" : "text.secondary",
                  }}
                >
                  {getIcon(item)}
                </ListItemIcon>
                <ListItemText
                  primary={item.menuName}
                  sx={{
                    opacity: open ? 1 : 0,
                    transition: "opacity 0.2s",
                    "& .MuiListItemText-primary": {
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: isSelected ? 600 : 400,
                      color: (theme) => isSelected ? theme.palette.text.primary : theme.palette.text.secondary,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Chat history section — only on Home, scrollable */}
      {selectedItem?.id === 1 && (
        <>
          <Divider sx={{ mx: 1, my: 0.5, flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
            <ChatTabs
              ref={chatTabsRef}
              sidebarOpen={open}
              activeTab={activeTab}
              chatContexts={chatContexts}
              onTabChange={onTabChange}
              onCloseTab={onCloseTab}
              onCreateNewTab={onCreateNewTab}
              onEditTab={onEditTab}
              onDeleteTab={onDeleteTab}
              onEditDefaultTab={onEditDefaultTab}
              defaultTabName={defaultTabName}
              truncateTabName={truncateTabName}
              selectedTabDetails={selectedTabDetails}
              isLoading={isChatLoading}
            />
          </Box>
        </>  
      )}
    </Drawer>
  );
};

DrawerMenu.propTypes = {
  open: PropTypes.bool,
  menuItems: PropTypes.array,
  selectedItem: PropTypes.object,
  handleItemClick: PropTypes.func,
  handleDrawerToggle: PropTypes.func,
  chatTabsRef: PropTypes.object,
  activeTab: PropTypes.string,
  chatContexts: PropTypes.array,
  onTabChange: PropTypes.func,
  onCloseTab: PropTypes.func,
  onCreateNewTab: PropTypes.func,
  onEditTab: PropTypes.func,
  onDeleteTab: PropTypes.func,
  onEditDefaultTab: PropTypes.func,
  defaultTabName: PropTypes.string,
  truncateTabName: PropTypes.func,
  selectedTabDetails: PropTypes.object,
  isChatLoading: PropTypes.bool,
};

export default DrawerMenu;
